import type { PostHog } from "posthog-js";

export const ANALYTICS_CONSENT_VERSION = "2026-05-04";
export const ANALYTICS_CONSENT_STORAGE_KEY = "ping_analytics_consent";
export const POSTHOG_CONSENT_STORAGE_KEY = "ping_posthog_consent_2026_05_04";
export const ANALYTICS_CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
export const OPEN_ANALYTICS_CONSENT_EVENT = "ping:open-analytics-consent";

export type AnalyticsConsentDecision = "accepted" | "rejected";

export type StoredAnalyticsConsent = {
  decision: AnalyticsConsentDecision;
  version: string;
  updatedAt: string;
  subject?: string;
  pendingSync?: boolean;
};

let hydratedAnonymousDecision: AnalyticsConsentDecision | null = null;

const clearStoredAnalyticsConsent = () => {
  try {
    window.localStorage.removeItem(ANALYTICS_CONSENT_STORAGE_KEY);
    window.localStorage.removeItem(POSTHOG_CONSENT_STORAGE_KEY);
  } catch {
    // The manager will ask again when storage is unavailable.
  }
};

export const isAnalyticsConsentCurrent = (updatedAt?: string | null) => {
  if (!updatedAt) return false;

  const updatedAtMs = Date.parse(updatedAt);
  const ageMs = Date.now() - updatedAtMs;

  // The server timestamp can be ahead of the browser clock; only expire old consent.
  return Number.isFinite(updatedAtMs) && ageMs <= ANALYTICS_CONSENT_MAX_AGE_MS;
};

export const readStoredAnalyticsConsent = (): StoredAnalyticsConsent | null => {
  try {
    const rawValue = window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue) as Partial<StoredAnalyticsConsent>;
    if (
      (parsed.decision !== "accepted" && parsed.decision !== "rejected") ||
      parsed.version !== ANALYTICS_CONSENT_VERSION ||
      typeof parsed.updatedAt !== "string" ||
      !isAnalyticsConsentCurrent(parsed.updatedAt) ||
      (parsed.subject !== undefined && typeof parsed.subject !== "string") ||
      (parsed.pendingSync !== undefined && typeof parsed.pendingSync !== "boolean")
    ) {
      clearStoredAnalyticsConsent();
      return null;
    }

    return parsed as StoredAnalyticsConsent;
  } catch {
    clearStoredAnalyticsConsent();
    return null;
  }
};

export const openAnalyticsConsentManager = () => {
  window.dispatchEvent(new Event(OPEN_ANALYTICS_CONSENT_EVENT));
};

export const storeAnalyticsConsent = (
  decision: AnalyticsConsentDecision,
  subject?: string,
  options: { updatedAt?: string; pendingSync?: boolean } = {},
): StoredAnalyticsConsent => {
  const storedConsent: StoredAnalyticsConsent = {
    decision,
    version: ANALYTICS_CONSENT_VERSION,
    updatedAt: options.updatedAt ?? new Date().toISOString(),
    pendingSync: options.pendingSync ?? true,
    ...(subject ? { subject } : {}),
  };

  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, JSON.stringify(storedConsent));
  } catch {
    // Consent still applies to the current page if storage is unavailable.
  }

  return storedConsent;
};

export const applyPostHogConsent = (
  posthog: PostHog,
  decision: AnalyticsConsentDecision,
  captureCurrentPage = true,
) => {
  if (decision === "accepted") {
    posthog.opt_in_capturing({ captureEventName: false });
  } else {
    posthog.opt_out_capturing();
  }

  if (captureCurrentPage) {
    posthog.capture("$pageview");
  }
};

export const suspendPostHogConsent = (posthog: PostHog) => {
  // Keep only application metadata; identity and account/group properties must be cleared.
  const appProperties = Object.fromEntries(
    ["environment", "hostname", "product", "ping_product"]
      .map((key) => [key, posthog.get_property(key)])
      .filter(([, value]) => value !== undefined),
  );
  posthog.clear_opt_in_out_capturing();
  posthog.reset(true);
  posthog.register(appProperties);
};

/**
 * Account-bound consent cannot safely be restored until /me identifies the
 * current account. Remove PostHog's separate cached decision before init so
 * the SDK starts pending with persistence disabled.
 */
export const preparePostHogConsent = () => {
  const storedConsent = readStoredAnalyticsConsent();
  if (storedConsent && !storedConsent.subject) return;

  try {
    window.localStorage.removeItem(POSTHOG_CONSENT_STORAGE_KEY);
  } catch {
    // PostHog also tolerates unavailable storage and remains non-persistent.
  }
};

/**
 * Restore only an anonymous landing-page choice before React renders. A choice
 * associated with an account waits for /me so it cannot leak across users on a
 * shared browser.
 */
export const hydrateAnonymousPostHogConsent = (posthog: PostHog) => {
  const storedConsent = readStoredAnalyticsConsent();
  if (!storedConsent || storedConsent.subject) return null;

  applyPostHogConsent(posthog, storedConsent.decision, false);
  hydratedAnonymousDecision = storedConsent.decision;
  return storedConsent.decision;
};

export const getHydratedAnonymousDecision = () => hydratedAnonymousDecision;
